import React from 'react';
import { Separator } from '@cotera/client/v0/components/ui/separator';
import {
  useStepInstanceSelection,
  WorkflowRunStepInstancesList,
} from '../workflow-run-step-instances';
import { filterRunStepView } from './filter-run-step-view';
import { debounceRunStepView } from './debounce-run-step-view';
import { bucketRunStepView } from './bucket-run-step-view';
import { agentRunStepView } from './agent-run-step-view';
import {
  PayloadBlock,
  Section,
  StepError,
  StepHeader,
} from './run-step-view-sections';
import type {
  WorkflowRunStepView,
  WorkflowRunStepViewCatalog,
  WorkflowRunStepViewProps,
} from './types';

/**
 * Default per-node step view: the node's status/error plus its input and output
 * rendered as JSON. Multi-instance steps (for-each fan-out) show an instance
 * list and drill into the selected instance's I/O.
 */
export const DefaultRunStepView: React.FC<WorkflowRunStepViewProps> = ({
  node,
  payload,
  initialInstanceKey,
}) => {
  const instances = payload?.instances ?? [];
  const { selectedInstanceKey, setSelectedInstanceKey, selectedInstance } =
    useStepInstanceSelection(instances, initialInstanceKey);

  const input =
    selectedInstance !== null ? selectedInstance.input : payload?.input ?? null;
  const output =
    selectedInstance !== null
      ? selectedInstance.output
      : payload?.output ?? null;
  const status =
    selectedInstance !== null ? selectedInstance.status : node.status;
  const error = selectedInstance !== null ? selectedInstance.error : node.error;

  return (
    <div className="h-full overflow-y-auto bg-background px-6 py-6 flex flex-col gap-6">
      <StepHeader name={node.name} status={status} />

      {error !== null && error !== '' ? (
        <Section title="Error">
          <StepError error={error} />
        </Section>
      ) : null}

      <WorkflowRunStepInstancesList
        instances={instances}
        selectedInstanceKey={selectedInstanceKey}
        onSelect={setSelectedInstanceKey}
      />

      <Section title="Input">
        <PayloadBlock
          value={input}
          emptyText="No input recorded for this step."
        />
      </Section>

      <Separator />

      <Section title="Output">
        <PayloadBlock
          value={output}
          emptyText="No output recorded for this step."
        />
      </Section>
    </div>
  );
};

const defaultStepView: WorkflowRunStepView = {
  key: 'default',
  matches: () => true,
  needsPayload: true,
  render: (props) => <DefaultRunStepView {...props} />,
};

/** Default catalog: filter, debounce and bucket nodes explain their own decision; others use JSON. */
export const DEFAULT_WORKFLOW_RUN_STEP_VIEW_CATALOG: WorkflowRunStepViewCatalog =
  {
    byNodeType: [
      agentRunStepView,
      filterRunStepView,
      debounceRunStepView,
      bucketRunStepView,
    ],
    fallback: defaultStepView,
  };

/** Resolves the step view for a node type, falling back to the JSON view. */
export function resolveRunStepView(
  catalog: WorkflowRunStepViewCatalog,
  nodeType: string
): WorkflowRunStepView {
  for (const entry of catalog.byNodeType) {
    if (entry.matches(nodeType)) {
      return entry;
    }
  }
  return catalog.fallback;
}
